import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, CheckCircle, AlertCircle, Package, RotateCcw, DollarSign, FileText, Download, Edit2, Save, X } from 'lucide-react';
import axios from 'axios';
import { Button, IconButton, StatusBadge } from '../common/ui';
import { useAuth } from '../../contexts/AuthContext';
import RepairActivity from './RepairActivity';
import InternalNotes from './InternalNotes';
import PaymentLinks from './PaymentLinks';
import { printPdfBlob } from '../../utils/pdfUtils';
import { 
  CustomerInformation, 
  RepairInformation,
  DeviceModules, 
  RepairDetails, 
  RepairActionButtons,
  ShippingInformation
} from './components';
import DeviceInfoSection from '../devices/components/DeviceInfoSection';
import FileUploadManager from '../common/ui/FileUploadManager';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

const RepairView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const repairHistoryRef = useRef();
  const internalNotesRef = useRef();
  const navigationTimeoutRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updating, setUpdating] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [draftDetails, setDraftDetails] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);

  // Global edit mode
  const [globalEditing, setGlobalEditing] = useState(false);
  const [globalSaving, setGlobalSaving] = useState(false);
  const [sectionErrors, setSectionErrors] = useState({});
  const [lockedSections, setLockedSections] = useState(new Set());
  const justSavedRef = useRef(false);
  const customerRef = useRef();
  const deviceRef = useRef();
  const repairInfoRef = useRef();
  const detailsRef = useRef();
  const shippingRef = useRef();
  
  // Repair data
  const [repairData, setRepairData] = useState({
    id: '',
    serial_number: '',
    product_type: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_return_address: '',
    help_scout_link: '',
    acceptance_notes: '',
    warranty_repair: false,
    temporary_import: false,
    report_to_team_required: false,
    shipped_items: [],
    device_id: '',
    created_by: '',
    assigned_to: '',
    status: '',
    internal_notes: '',
    reported_date: '',
    started_date: '',
    completed_date: '',
    final_resolution: '',
    woocommerce_link: '',
    created_by_name: '',
    assigned_to_name: '',
    delivery_method: '',
    delivery_method_other: '',
    tracking_id: '',
    notify_customer: true
  });

  // Device data
  const [deviceData, setDeviceData] = useState(null);
  const [productConfig, setProductConfig] = useState({
    product: null,
    productAttributes: [],
    assignedModules: [],
    moduleAttributesMap: {}
  });
  const [deviceAttributeValues, setDeviceAttributeValues] = useState({});
  const [moduleAttributeValues, setModuleAttributeValues] = useState({});

  // Reference data
  const [users, setUsers] = useState([]);

  // Memoized callbacks to prevent unnecessary re-renders in child components
  const handleActivityRefresh = useCallback(() => {
    // Refresh activity when a new note is added
    if (repairHistoryRef.current) {
      repairHistoryRef.current.refresh();
    }
  }, []);

  const handleInternalNotesRefresh = useCallback(() => {
    // Refresh internal notes when a new comment is added
    if (internalNotesRef.current) {
      internalNotesRef.current.refresh();
    }
  }, []);

  const handleModuleRepairSaved = useCallback(() => {
    // Refresh activity when a module repair is saved
    if (repairHistoryRef.current) {
      repairHistoryRef.current.refresh();
    }
    // Also refresh internal notes
    if (internalNotesRef.current) {
      internalNotesRef.current.refresh();
    }
    // Refresh repair and device data (serial number may have changed during motherboard exchange)
    fetchRepair();
  }, []);

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([
        fetchRepair(),
        fetchUsers()
      ]);
    };
    
    initialize();
  }, [id]);

  // Fetch device data when device_id is available
  useEffect(() => {
    if (repairData.device_id) {
      fetchDevice();
    }
  }, [repairData.device_id]);

  // Fetch product configuration when device data is available
  useEffect(() => {
    if (deviceData?.model || deviceData?.product_name) {
      fetchProductConfiguration(deviceData.product_name || deviceData.model);
    }
  }, [deviceData]);

  // Cleanup navigation timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const fetchRepair = async () => {
    try {
      const response = await axios.get(`/api/repairs/${id}`);
      const repair = response.data;
      
      console.log('Fetched repair data:', repair);
      console.log('Device serial from API:', repair.device_serial);
      console.log('Device model from API:', repair.device_model);
      
      // Map API fields to component fields for consistency with RepairNew
      const mappedData = {
        ...repair,
        serial_number: repair.device_serial || '',
        product_type: repair.device_model || '',
        customer_name: repair.customer_name || '',
        customer_email: repair.customer_email || '',
        customer_phone: repair.customer_phone || '',
        customer_return_address: repair.customer_return_address || '',
        acceptance_notes: repair.acceptance_notes || '',
        warranty_repair: repair.warranty_repair || false,
        temporary_import: repair.temporary_import || false,
        report_to_team_required: repair.report_to_team_required || false,
        shipped_items: repair.shipped_items || [],
        help_scout_link: repair.help_scout_link || '',
        internal_notes: repair.internal_notes || '',
        final_resolution: repair.final_resolution || '',
        woocommerce_link: repair.woocommerce_link || '',
        created_by_name: repair.created_by_name || '',
        assigned_to_name: repair.assigned_to_name || '',
        notify_customer: repair.notify_customer !== undefined ? repair.notify_customer : true
      };
      
      console.log('Mapped repair data:', mappedData);
      console.log('Mapped serial_number:', mappedData.serial_number);
      
      setRepairData(mappedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching repair:', err);
      setError('Failed to fetch repair');
      setLoading(false);
    }
  };

  const fetchDevice = async () => {
    try {
      const response = await axios.get(`/api/devices/${repairData.device_id}`);
      const device = response.data;
      setDeviceData(device);
      
      // Parse attributes into a map for legacy attributes display
      const attrMap = {};
      (device.attributes || []).forEach(attr => {
        attrMap[attr.name] = attr.value;
      });
      setDeviceAttributeValues(attrMap);
      
      // Parse module attributes into a map
      const moduleAttrMap = {};
      (device.moduleAttributes || []).forEach(attr => {
        const key = `${attr.module_id}_${attr.attribute_name}`;
        moduleAttrMap[key] = attr.attribute_value;
      });
      setModuleAttributeValues(moduleAttrMap);
    } catch (err) {
      console.error('Error fetching device:', err);
      // Don't set error state here as device data is supplementary
    }
  };

  const fetchProductConfiguration = async (productName) => {
    try {
      const response = await axios.get(`/api/devices/product-config/${encodeURIComponent(productName)}`, {
        params: { includeHidden: 'true' }
      });
      
      setProductConfig({
        product: response.data.product,
        productAttributes: response.data.productAttributes || [],
        assignedModules: response.data.assignedModules || [],
        moduleAttributesMap: response.data.moduleAttributesMap || {}
      });
    } catch (err) {
      console.error('Error fetching product configuration:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users/basic');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRepairData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save customer information updates
  const handleCustomerInfoSave = async (customerData) => {
    try {
      setError('');
      setSuccess('');
      
      // Update repair record with customer information
      await axios.put(`/api/repairs/${id}`, {
        customer_name: customerData.customer_name,
        customer_email: customerData.customer_email,
        customer_phone: customerData.customer_phone,
        customer_return_address: customerData.customer_return_address,
        help_scout_link: customerData.help_scout_link,
        woocommerce_link: customerData.woocommerce_link,
        notify_customer: customerData.notify_customer
      });
      
      // Refresh repair history
      if (repairHistoryRef.current) {
        repairHistoryRef.current.refresh();
      }
      
      setSuccess('Customer information saved successfully');
      await fetchRepair(); // Refresh data
    } catch (err) {
      console.error('Error saving customer information:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || 'Failed to save customer information';
      setError(errorMsg);
      throw err; // Re-throw so component can handle it
    }
  };

  // Save device information updates
  const handleDeviceInfoSave = async (deviceDataToSave) => {
    try {
      setError('');
      setSuccess('');
      
      // Update the device record - include all required fields
      await axios.put(`/api/devices/${repairData.device_id}`, {
        serial_number: deviceDataToSave.serial_number,
        product_id: deviceDataToSave.product_id || deviceData?.product_id, // Required by backend
        status: deviceDataToSave.status,
        purchase_date: deviceDataToSave.purchase_date !== undefined ? deviceDataToSave.purchase_date : deviceData?.purchase_date,
        warranty_expiry: deviceData?.warranty_expiry,
        notes: deviceDataToSave.notes !== undefined ? deviceDataToSave.notes : deviceData?.notes,
        performed_by: user?.id || null,
        repair_id: id // Link change to this repair
      });
      
      // Refresh device data
      await fetchDevice();
      
      // Refresh repair history
      if (repairHistoryRef.current) {
        repairHistoryRef.current.refresh();
      }
      
      setSuccess('Device information saved successfully');
    } catch (err) {
      console.error('Error saving device information:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || 'Failed to save device information';
      setError(errorMsg);
      throw err; // Re-throw so component can handle it
    }
  };

  // Change the device linked to this repair (device swap)
  const handleChangeDevice = async (newDeviceId) => {
    try {
      setError('');
      setSuccess('');

      await axios.put(`/api/repairs/${id}`, {
        device_id: newDeviceId,
        performed_by: user?.id || null
      });

      // Refresh repair data — the useEffect on repairData.device_id will auto-refresh device data
      await fetchRepair();

      // Refresh repair history
      if (repairHistoryRef.current) {
        repairHistoryRef.current.refresh();
      }

      setSuccess('Device changed successfully');
    } catch (err) {
      console.error('Error changing device:', err);
      const errorMsg = err.response?.data?.error || 'Failed to change device';
      setError(errorMsg);
      throw err;
    }
  };

  // Save repair information updates
  const handleRepairInfoSave = async (repairInfoData) => {
    try {
      setError('');
      setSuccess('');
      
      // Update repair record with acceptance notes and new fields
      const updateData = {
        acceptance_notes: repairInfoData.acceptance_notes,
        warranty_repair: repairInfoData.warranty_repair || false,
        temporary_import: repairInfoData.temporary_import || false,
        report_to_team_required: repairInfoData.report_to_team_required || false,
        shipped_items: repairInfoData.shipped_items || [],
        shipped_items_other: repairInfoData.shipped_items_other || ''
      };
      
      await axios.put(`/api/repairs/${id}`, updateData);
      
      // Refresh repair history
      if (repairHistoryRef.current) {
        repairHistoryRef.current.refresh();
      }
      
      setSuccess('Repair information saved successfully');
      await fetchRepair(); // Refresh data
    } catch (err) {
      console.error('Error saving repair information:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || 'Failed to save repair information';
      setError(errorMsg);
      throw err; // Re-throw so component can handle it
    }
  };

  // Save repair details (final resolution only)
  const handleRepairDetailsSave = async (detailsData) => {
    try {
      setError('');
      setSuccess('');
      
      const updateData = {
        final_resolution: detailsData.final_resolution
      };
      
      await axios.put(`/api/repairs/${id}`, updateData);
      
      if (repairHistoryRef.current) {
        repairHistoryRef.current.refresh();
      }
      
      setSuccess('Customer notes saved successfully');
      await fetchRepair();
    } catch (err) {
      console.error('Error saving customer notes:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || 'Failed to save customer notes';
      setError(errorMsg);
      throw err;
    }
  };

  // Save shipping information
  const handleShippingSave = async (shippingData) => {
    try {
      setError('');
      setSuccess('');
      
      const updateData = {
        delivery_method: shippingData.delivery_method,
        delivery_method_other: shippingData.delivery_method_other,
        tracking_id: shippingData.tracking_id
      };
      
      await axios.put(`/api/repairs/${id}`, updateData);
      
      if (repairHistoryRef.current) {
        repairHistoryRef.current.refresh();
      }
      
      setSuccess('Shipping information saved successfully');
      await fetchRepair();
    } catch (err) {
      console.error('Error saving shipping information:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || 'Failed to save shipping information';
      setError(errorMsg);
      throw err;
    }
  };

  // Append text to final resolution
  const handleAppendToResolution = async (textToAppend) => {
    if (!textToAppend) return;

    try {
      // In global edit mode, read from the details ref to avoid overwriting unsaved edits
      let currentResolution;
      if (globalEditing && detailsRef.current?.getEditedData) {
        currentResolution = detailsRef.current.getEditedData().final_resolution || '';
      } else {
        currentResolution = draftDetails.final_resolution !== undefined 
          ? draftDetails.final_resolution 
          : (repairData.final_resolution || '');
      }
        
      const separator = currentResolution.length > 0 ? '\n' : '';
      const newResolution = currentResolution + separator + textToAppend;

      // Update local state
      setRepairData(prev => ({
        ...prev,
        final_resolution: newResolution
      }));
      
      // Update draft details to match, ensuring consistency
      setDraftDetails(prev => ({
        ...prev,
        final_resolution: newResolution
      }));
      
      // Force RepairDetails to update its editing state
      setDetailsRefreshKey(prev => prev + 1);

      // Persist to backend immediately
      // We only update the final_resolution field to avoid side effects
      await axios.put(`/api/repairs/${id}`, {
        final_resolution: newResolution
      });
      
      // We don't set success message here to avoid cluttering the UI for this background action,
      // or we could show a subtle one. For now, just silent update is better as it's an automation.
      
    } catch (err) {
      console.error('Error appending to resolution:', err);
      // Use setError only if it's critical, or just log it. 
      // Given user can still edit it manually, maybe just log error to console is safer than disrupting workflow,
      // but sticking to standard error handling is probably better.
      setError('Failed to auto-update final resolution');
    }
  };

  // Save and change status
  const handleStatusChange = async (newStatus, additionalData = {}, shouldNavigate = false) => {
    try {
      setUpdating(true);
      setError('');
      setSuccess('');
      setValidationErrors({});

      // Merge all drafts with current data and additional data
      // Priority: additionalData > shipping draft > customer draft > draftDetails > repairData
      const shippingDraft = shippingRef.current?.getEditedData?.() || {};
      const customerDraft = customerRef.current?.getEditedData?.() || {};
      const currentData = {
        ...repairData,
        ...draftDetails,
        ...customerDraft,
        ...shippingDraft,
        ...additionalData
      };
      
      // Check if final_resolution is required for this status
      const statusesRequiringResolution = ['repaired', 'waiting_for_payment', 'ready_for_shipping', 'completed'];
      if (statusesRequiringResolution.includes(newStatus)) {
        const finalResolution = currentData.final_resolution;
        if (!finalResolution || finalResolution.trim() === '') {
          setError('Final resolution is required before moving to this status. Please fill in the "Final resolution" field under Customer visible notes.');
          setValidationErrors(prev => ({ ...prev, final_resolution: 'Final resolution is required' }));
          setUpdating(false);
          // Scroll to the final resolution section
          const finalResolutionSection = document.querySelector('[name="final_resolution"]');
          if (finalResolutionSection) {
            finalResolutionSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            finalResolutionSection.focus();
          }
          return;
        }

        // When the repair is flagged "Report to the team", require a reported note
        // before it can be closed. The server enforces this too; this is for feedback.
        if (currentData.report_to_team_required) {
          try {
            const { data: comments } = await axios.get(`/api/repairs/${id}/comments`);
            const hasReportedNote = (comments || []).some((c) => c.reported_at);
            if (!hasReportedNote) {
              setError('This repair is marked "Report to the team". Report an internal note to the team (megaphone icon in Internal Notes) before moving it to this status.');
              setUpdating(false);
              const notesSection = document.getElementById('internal-notes');
              if (notesSection) {
                notesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              return;
            }
          } catch (checkErr) {
            // If the check itself fails, fall through and let the server decide
            console.error('Could not verify reported notes:', checkErr);
          }
        }
      }

      // Check if delivery_method is required for completed status
      if (newStatus === 'completed') {
        const deliveryMethod = currentData.delivery_method;
        if (!deliveryMethod || deliveryMethod.trim() === '') {
          setError('Delivery method is required before moving to completed status. Please fill in the "Delivery Method" field under Shipping Information.');
          setValidationErrors(prev => ({ ...prev, delivery_method: 'Delivery method is required' }));
          setUpdating(false);
          // Scroll to the delivery method section
          const deliveryMethodSection = document.querySelector('[name="delivery_method"]');
          if (deliveryMethodSection) {
            deliveryMethodSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            deliveryMethodSection.focus();
          }
          return;
        }
        
        // If delivery_method is "other", validate that delivery_method_other is provided
        if (deliveryMethod === 'other') {
          const deliveryMethodOther = currentData.delivery_method_other;
          if (!deliveryMethodOther || deliveryMethodOther.trim() === '') {
            setError('Please specify the delivery method when "Other" is selected.');
            setValidationErrors(prev => ({ ...prev, delivery_method_other: 'Please specify the delivery method' }));
            setUpdating(false);
            // Scroll to the delivery method other section
            const deliveryMethodOtherSection = document.querySelector('[name="delivery_method_other"]');
            if (deliveryMethodOtherSection) {
              deliveryMethodOtherSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
              deliveryMethodOtherSection.focus();
            }
            return;
          }
        }
      }
      
      // Clean data - remove client-side only fields
      const submitData = { ...currentData };
      delete submitData.serial_number;
      delete submitData.product_type;
      delete submitData.created_by_name;
      delete submitData.assigned_to_name;
      delete submitData.device_serial;
      delete submitData.device_model;
      
      await axios.put(`/api/repairs/${id}`, {
        ...submitData,
        status: newStatus,
        performed_by: user?.id || null,
        ...additionalData
      });
      
      // Refresh repair history
      if (repairHistoryRef.current) {
        repairHistoryRef.current.refresh();
      }
      
      setSuccess(`Status changed to ${newStatus}`);
      await fetchRepair(); // Refresh data
      setUpdating(false);
      setDraftDetails({}); // Clear draft details after successful save
      
      // Reset editing state on all sections
      customerRef.current?.reset?.();
      shippingRef.current?.reset?.();
      detailsRef.current?.reset?.();
      
      // Navigate if requested (e.g., after finishing repair)
      if (shouldNavigate) {
        navigationTimeoutRef.current = setTimeout(() => {
          navigate('/repairs');
        }, 1500);
      }
    } catch (err) {
      console.error('Error changing status:', err);
      const errorMsg = err.response?.data?.error || 'Failed to change status';
      setError(errorMsg);
      setUpdating(false);
    }
  };

  // Specific status change handlers
  const handleStartRepair = () => handleStatusChange('work_in_progress', {
    started_date: new Date().toISOString(),
    assigned_to: user?.id || null
  });
  
  const handleFinishRepair = () => handleStatusChange('repaired', {
    completed_date: new Date().toISOString()
  }, true); // Navigate to /repairs after successful status change
  
  const handleClarificationRequired = () => handleStatusChange('clarification_required');
  const handleBackToQueue = () => handleStatusChange('waiting_for_repair');
  const handleMoveToQueue = () => handleStatusChange('waiting_for_repair');
  const handleAwaitingPayment = () => handleStatusChange('waiting_for_payment');
  const handleReadyForShipping = () => handleStatusChange('ready_for_shipping');
  const handleCompleted = () => handleStatusChange('completed');
  const handleReopen = () => handleStatusChange('work_in_progress');

  // Handle module actions (repair/exchange)
  const handleModuleAction = (action, module) => {
    console.log(`${action} module:`, module);
  };

  // Reset justSavedRef after status changes
  useEffect(() => {
    justSavedRef.current = false;
  }, [repairData.status]);

  // Global edit handlers
  const handleGlobalEdit = () => {
    setGlobalEditing(true);
    setLockedSections(new Set());
    setSectionErrors({});
  };

  const handleGlobalCancel = () => {
    const refs = { customer: customerRef, device: deviceRef, repair: repairInfoRef, details: detailsRef, shipping: shippingRef };
    const hasAnyChanges = Object.values(refs).some(r => r.current?.hasChanges?.());
    
    if (hasAnyChanges) {
      if (!window.confirm('Discard all unsaved changes?')) return;
    }
    
    Object.values(refs).forEach(r => r.current?.reset?.());
    setGlobalEditing(false);
    setLockedSections(new Set());
    setSectionErrors({});
    setDraftDetails({});
  };

  const handleGlobalSave = async () => {
    setGlobalSaving(true);
    setError('');
    setSuccess('');
    setSectionErrors({});

    const sections = [
      { key: 'customer', ref: customerRef, handler: handleCustomerInfoSave },
      { key: 'device', ref: deviceRef, handler: handleDeviceInfoSave },
      { key: 'repair', ref: repairInfoRef, handler: handleRepairInfoSave },
      { key: 'details', ref: detailsRef, handler: handleRepairDetailsSave },
      { key: 'shipping', ref: shippingRef, handler: handleShippingSave },
    ];

    // Only process unlocked sections with changes
    const toSave = sections.filter(s => 
      !lockedSections.has(s.key) && s.ref.current?.hasChanges?.()
    );

    // Validate only changed sections
    const errors = {};
    for (const s of toSave) {
      const result = s.ref.current?.validate?.();
      if (result && !result.valid) {
        errors[s.key] = result.error;
      }
    }

    if (Object.keys(errors).length > 0) {
      setSectionErrors(errors);
      setError('Please fix validation errors before saving.');
      setGlobalSaving(false);
      return;
    }

    // Save all changed sections in parallel
    const results = await Promise.allSettled(
      toSave.map(async (s) => {
        const data = s.ref.current.getEditedData();
        await s.handler(data);
        return s.key;
      })
    );

    const succeeded = [];
    const failed = {};
    results.forEach((result, idx) => {
      if (result.status === 'fulfilled') {
        succeeded.push(toSave[idx].key);
      } else {
        const errMsg = result.reason?.response?.data?.error || result.reason?.message || 'Save failed';
        failed[toSave[idx].key] = errMsg;
      }
    });

    if (Object.keys(failed).length === 0) {
      // All succeeded
      justSavedRef.current = true;
      setGlobalEditing(false);
      setLockedSections(new Set());
      setSectionErrors({});
      setDraftDetails({});
      setSuccess(toSave.length > 0 ? 'All changes saved successfully.' : 'No changes to save.');
    } else {
      // Partial failure - lock succeeded sections
      const newLocked = new Set([...lockedSections, ...succeeded]);
      setLockedSections(newLocked);
      setSectionErrors(failed);
      const failCount = Object.keys(failed).length;
      setError(`${failCount} section${failCount > 1 ? 's' : ''} failed to save.`);
    }

    setGlobalSaving(false);
  };

  const handleGeneratePDF = async () => {
    try {
      setGeneratingPDF(true);
      setError('');
      setSuccess('');

      // Call the backend API to generate PDF server-side
      const response = await axios.post(`/api/repairs/${id}/generate-pdf?download=true`, {}, {
        responseType: 'blob' // Important: tell axios to expect binary data
      });

      // Check if the response is actually an error (JSON) instead of a PDF
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      const blob = response.data;
      await printPdfBlob(blob);

      const contentDisposition = response.headers['content-disposition'];
      let filename = `repair-${id}-report.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      setSuccess(`PDF report generated successfully: ${filename}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Handle blob error responses
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          setError(errorData.error || 'Failed to generate PDF');
        } catch (e) {
          setError(error.message || 'Failed to generate PDF');
        }
      } else {
        setError(error.message || error.response?.data?.error || 'Failed to generate PDF');
      }
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setGeneratingPDF(true);
      setError('');
      setSuccess('');

      const response = await axios.post(`/api/repairs/${id}/generate-pdf?download=true`, {}, {
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      const blob = response.data;
      const serial = repairData.serial_number || 'unknown';
      const filename = `#${id} Repair report ${serial}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`PDF downloaded: ${filename}`);
    } catch (error) {
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          setError(errorData.error || 'Failed to generate PDF');
        } catch (e) {
          setError(error.message || 'Failed to generate PDF');
        }
      } else {
        setError(error.message || error.response?.data?.error || 'Failed to generate PDF');
      }
    } finally {
      setGeneratingPDF(false);
    }
  };

  const getActionButtons = () => {
    const { status } = repairData;
    const buttons = {};
    
    // Back button - always shown
    buttons.backButton = (
      <Button
        onClick={() => navigate(-1)}
        variant="ghost"
        size="xs"
        icon={ArrowLeft}
        className="flex-shrink-0 px-0"
      >
        Back
      </Button>
    );

    
    // Status dropdown - disabled during editing
    buttons.statusDropdown = (
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) {
            handleStatusChange(e.target.value);
            e.target.value = '';
          }
        }}
        disabled={updating || globalEditing}
        title={globalEditing ? 'Save or cancel changes first' : undefined}
        className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-naviter-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">or choose a status manually</option>
        <option value="draft">Draft</option>
        <option value="backlog">Backlog</option>
        <option value="waiting_for_repair">Waiting for repair</option>
        <option value="clarification_required">Clarification Required</option>
        <option value="waiting_for_dev_input">Waiting for dev input</option>
        <option value="waiting_for_customer_response">Waiting for customer response</option>
        <option value="work_in_progress">Work in progress</option>
        <option value="repaired">Repaired</option>
        <option value="ready_for_shipping">Ready for shipping</option>
        <option value="waiting_for_payment">Waiting for payment</option>
        <option value="completed">Completed</option>
      </select>
    );
    
    // Action buttons and CTA based on status
    // When globalEditing is active, all status actions are disabled
    const actionsDisabled = updating || globalEditing;
    const actionsTitle = globalEditing ? 'Save or cancel changes first' : undefined;
    
    switch (status) {
      case 'draft':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            disabled={globalEditing}
            title={actionsTitle || "Download PDF"}
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
            disabled={globalEditing}
          >
            Print PDF
          </Button>,
          <Button
            key="clarification"
            onClick={handleClarificationRequired}
            variant="secondary"
            size="xs"
            icon={AlertCircle}
            loading={updating}
          >
            Clarification Required
          </Button>
        ];
        buttons.ctaButton = (
          <Button
            onClick={() => handleStatusChange('waiting_for_repair')}
            variant="primary"
            size="md"
            icon={Play}
            loading={updating}
          >
            Add to Queue
          </Button>
        );
        break;
        
      case 'backlog':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="backtoqueue"
            onClick={handleMoveToQueue}
            variant="secondary"
            size="xs"
            icon={RotateCcw}
            loading={updating}
          >
            Back to Queue
          </Button>
        ];
        buttons.ctaButton = (
          <Button
            onClick={handleStartRepair}
            variant="primary"
            size="md"
            icon={Play}
            loading={updating}
          >
            Start Repair
          </Button>
        );
        break;
        
      case 'waiting_for_repair':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="clarification"
            onClick={handleClarificationRequired}
            variant="secondary"
            size="xs"
            icon={AlertCircle}
            loading={updating}
          >
            Clarification Required
          </Button>
        ];
        buttons.ctaButton = (
          <Button
            onClick={handleStartRepair}
            variant="primary"
            size="md"
            icon={Play}
            loading={updating}
          >
            Start Repair
          </Button>
        );
        break;
        
      case 'clarification_required':
      case 'waiting_for_dev_input':
      case 'waiting_for_customer_response':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="backtoqueue"
            onClick={handleBackToQueue}
            variant="secondary"
            size="xs"
            icon={RotateCcw}
            loading={updating}
          >
            Back to Queue
          </Button>
        ];
        buttons.ctaButton = (
          <Button
            onClick={handleStartRepair}
            variant="primary"
            size="md"
            icon={Play}
            loading={updating}
          >
            Start Repair
          </Button>
        );
        break;
        
      case 'work_in_progress':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="clarification"
            onClick={handleClarificationRequired}
            variant="secondary"
            size="xs"
            icon={AlertCircle}
            loading={updating}
          >
            Clarification Required
          </Button>
        ];
        buttons.ctaButton = (
          <Button
            onClick={handleFinishRepair}
            variant="primary"
            size="md"
            icon={CheckCircle}
            loading={updating}
          >
            Finish Repair
          </Button>
        );
        break;
        
      case 'repaired':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="readyforshipping"
            onClick={handleReadyForShipping}
            variant="secondary"
            size="xs"
            icon={Package}
            loading={updating}
          >
            Ready for Shipping
          </Button>,
          <Button
            key="awaitingpayment"
            onClick={handleAwaitingPayment}
            variant="secondary"
            size="xs"
            icon={DollarSign}
            loading={updating}
          >
            Awaiting Payment
          </Button>
        ];
        buttons.ctaButton = null; // No CTA for repaired status
        break;
        
      case 'ready_for_shipping':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="clarification"
            onClick={handleClarificationRequired}
            variant="secondary"
            size="xs"
            icon={AlertCircle}
            loading={updating}
          >
            Clarification Required
          </Button>
        ];
        buttons.ctaButton = (
          <Button
            onClick={handleCompleted}
            variant="primary"
            size="md"
            icon={CheckCircle}
            loading={updating}
          >
            Completed
          </Button>
        );
        break;
        
      case 'waiting_for_payment':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="clarification"
            onClick={handleClarificationRequired}
            variant="secondary"
            size="xs"
            icon={AlertCircle}
            loading={updating}
          >
            Clarification Required
          </Button>
        ];
        buttons.ctaButton = (
          <Button
            onClick={handleCompleted}
            variant="primary"
            size="md"
            icon={CheckCircle}
            loading={updating}
          >
            Completed
          </Button>
        );
        break;
        
      case 'completed':
        buttons.actionButtons = [
          <IconButton
            key="download-pdf"
            onClick={handleDownloadPDF}
            icon={Download}
            variant="ghost"
            size="sm"
            loading={generatingPDF}
            title="Download PDF"
          />,
          <Button
            key="pdf"
            onClick={handleGeneratePDF}
            variant="secondary"
            size="xs"
            icon={FileText}
            loading={generatingPDF}
          >
            Print PDF
          </Button>,
          <Button
            key="clarification"
            onClick={handleClarificationRequired}
            variant="secondary"
            size="xs"
            icon={AlertCircle}
            loading={updating}
          >
            Clarification Required
          </Button>
        ];
        buttons.ctaButton = null; // No CTA for completed status
        break;
        
      default:
        // Fallback for any unexpected status
        buttons.actionButtons = [];
        buttons.ctaButton = null;
    }
    
    return buttons;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not set';
    return `€${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-3">
            {/* Header */}
            <div className="space-y-4">
              {/* 1. Title and status */}
              <div className="flex items-center gap-3">
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                  Repair #{repairData.id}
                </h1>
                <StatusBadge status={repairData.status} variant="repair" />
              </div>
              
              {/* 2. Action Buttons */}
              <RepairActionButtons {...getActionButtons()} actionsDisabled={globalEditing} />

            </div>

            {/* Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
                  <div className="ml-2">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <div className="flex">
                  <CheckCircle className="h-4 w-4 text-green-400 mt-0.5" />
                  <div className="ml-2">
                    <div className="text-sm text-green-700">{success}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit controls - separate row, right-aligned */}
            <div className="flex items-center justify-end gap-2">
              {globalEditing ? (
                <>
                  <Button onClick={handleGlobalSave} variant="primary" size="xs" icon={Save} loading={globalSaving}>Save all</Button>
                  <Button onClick={handleGlobalCancel} variant="ghost" size="xs" icon={X} disabled={globalSaving}>Cancel</Button>
                </>
              ) : (
                <button onClick={handleGlobalEdit} className="flex items-center gap-1 text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50 transition-colors text-xs font-medium" title="Edit all sections">
                  <Edit2 className="h-4 w-4" />
                  Edit all
                </button>
              )}
            </div>

            {/* Main Content */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-4 space-y-3">
                
                {/* 1. Customer Information */}
                <CustomerInformation 
                  ref={customerRef}
                  repairData={repairData} 
                  readOnly={false}
                  onSave={handleCustomerInfoSave}
                  showLinks={true}
                  externalEditMode={globalEditing ? (lockedSections.has('customer') ? 'view' : 'edit') : undefined}
                />
                {sectionErrors.customer && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{sectionErrors.customer}</div>
                )}

                {/* 2. Device Information */}
                {deviceData ? (
                  <DeviceInfoSection 
                    ref={deviceRef}
                    deviceData={deviceData}
                    readOnly={false}
                    onSave={handleDeviceInfoSave}
                    onChangeDevice={handleChangeDevice}
                    externalEditMode={globalEditing ? (lockedSections.has('device') ? 'view' : 'edit') : undefined}
                  />
                ) : (
                  <div className="text-gray-500 text-sm">Loading device information...</div>
                )}
                {sectionErrors.device && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{sectionErrors.device}</div>
                )}

                {/* 3. Repair Information + 4. Customer Visible Notes - side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="flex flex-col [&>*:first-child]:flex-1">
                    <RepairInformation 
                      ref={repairInfoRef}
                      repairData={repairData}
                      readOnly={false}
                      onSave={handleRepairInfoSave}
                      externalEditMode={globalEditing ? (lockedSections.has('repair') ? 'view' : 'edit') : undefined}
                    />
                    {sectionErrors.repair && (
                      <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{sectionErrors.repair}</div>
                    )}
                  </div>

                  <div className="flex flex-col [&>*:first-child]:flex-1">
                    <RepairDetails 
                      ref={detailsRef}
                      repairData={repairData} 
                      onSave={handleRepairDetailsSave}
                      readOnly={false}
                      showFinalResolution={true}
                      onDraftChange={setDraftDetails}
                      validationErrors={validationErrors}
                      refreshKey={detailsRefreshKey}
                      externalEditMode={globalEditing ? (lockedSections.has('details') ? 'view' : 'edit') : undefined}
                    />
                    {sectionErrors.details && (
                      <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{sectionErrors.details}</div>
                    )}
                  </div>
                </div>

                {/* Edit controls - above modules */}
                <div className="flex items-center justify-end gap-2">
                  {globalEditing ? (
                    <>
                      <Button onClick={handleGlobalSave} variant="primary" size="xs" icon={Save} loading={globalSaving}>Save all</Button>
                      <Button onClick={handleGlobalCancel} variant="ghost" size="xs" icon={X} disabled={globalSaving}>Cancel</Button>
                    </>
                  ) : (
                    <button onClick={handleGlobalEdit} className="flex items-center gap-1 text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50 transition-colors text-xs font-medium" title="Edit all sections">
                      <Edit2 className="h-4 w-4" />
                      Edit all
                    </button>
                  )}
                </div>

                {/* 5. Modules */}
                <DeviceModules 
                  repairData={repairData} 
                  showActions={true}
                  showEditButton={false}
                  productConfig={productConfig}
                  deviceStatus={deviceData?.status || 'active'}
                  deviceAttributeValues={deviceAttributeValues}
                  onModuleAction={handleModuleAction}
                  onRepairSaved={handleModuleRepairSaved}
                  onAppendResolution={handleAppendToResolution}
                />

              </div>
            </div>

            {/* Action Buttons - Bottom (outside white card, in gray area) */}
            <div>
              <RepairActionButtons {...getActionButtons()} actionsDisabled={globalEditing} />
            </div>

            {/* 9. Repair Activity (now includes Add Comment) */}
            <div>
              <RepairActivity
                ref={repairHistoryRef}
                deviceId={repairData.device_id}
                repairId={id}
              />
            </div>
          </div>

          {/* Right Column - Shipping, Attachments & Internal Notes */}
          <div className="lg:col-span-1 space-y-6 pb-8">
            {/* Shipping Information */}
            {(repairData.delivery_method || repairData.tracking_id || ['repaired', 'waiting_for_payment', 'ready_for_shipping', 'completed'].includes(repairData.status)) && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-6">
                  <ShippingInformation
                    ref={shippingRef}
                    repairData={repairData}
                    onSave={handleShippingSave}
                    readOnly={false}
                    validationErrors={validationErrors}
                    externalEditMode={globalEditing ? (lockedSections.has('shipping') ? 'view' : 'edit') : undefined}
                  />
                  {sectionErrors.shipping && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{sectionErrors.shipping}</div>
                  )}
                </div>
              </div>
            )}

            {/* Attachments */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Attachments</h3>
                <FileUploadManager
                  entityType="repairs"
                  entityId={id}
                  compact={true}
                />
              </div>
            </div>

            {/* Payment Links */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-6">
                <PaymentLinks
                  repairId={id}
                  customerEmail={repairData.customer_email}
                  deviceModel={repairData.product_type}
                  serialNumber={repairData.serial_number}
                  helpScoutLink={repairData.help_scout_link}
                  onRefresh={handleActivityRefresh}
                />
              </div>
            </div>

            {/* Internal Notes */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-6">
                <InternalNotes
                  ref={internalNotesRef}
                  repairId={id}
                  onRefresh={handleActivityRefresh}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairView;